import React from 'react'
import { Segment, Checkbox, Form, Button, Grid, Input } from 'semantic-ui-react'
import styled from 'styled-components'
import * as R from 'ramda'

import { CrownBadge } from 'components/ProFeatureLabel'
import { TooltipLabelWithoutIcon } from 'components/TooltipLabelWithoutIcon'
import useFeatureToggle from 'hooks/useFeatureToggle'
import useUser from 'hooks/useUser'
import useSaveUserActivityLog, { ACTION_NAME } from 'hooks/useSaveUserActivityLog'
import { FollowUpChoiceInput } from './FollowUpChoiceInput'

const FILE_UPLOAD_LEARN_MORE_LINK =
  'https://help.simplesat.io/en/articles/2776705-survey-questions#file-upload-questions'

const LOCKED_ITEM_POPUP_POSITION = 'top center'

const FollowUpQuestionTypeGroup = styled(Button.Group)`
  flex-wrap: wrap;

  &.ui.buttons > *:not(:first-child) {
    margin-left: -1px;
  }
`

const FOLLOW_UP_TYPE_BUTTON_HEIGHT = '40px'

const FollowUpQuestionTypeButton = styled(Button)`
  position: relative;
  height: ${FOLLOW_UP_TYPE_BUTTON_HEIGHT};
  background-color: ${(props) => (props.active ? '#e6f2ff' : 'white')} !important;
  color: ${(props) => (props.active ? '#007eff' : '')} !important;
  font-weight: 400 !important;
  border: 1px solid ${(props) => (props.active ? '#007eff' : '#dedede')} !important;
  z-index: ${(props) => (props.active ? 1 : 0)};

  :hover {
    border-color: #007eff !important;
    background-color: #e6f2ff !important;
    z-index: 2;
  }
`

const LockedFollowUpQuestionTypeButton = styled(FollowUpQuestionTypeButton)`
  &.button {
    width: 100%;
    color: #9b9b9b !important;
    border-radius: 0 !important;
  }

  &.button:hover {
    border-color: #007eff !important;
  }
`

const StyledSegment = styled(Segment)`
  padding: 32px 16px !important;
`

const StyledFollowupQuestionFormField = styled(Form.Field)`
  margin: 7px 0 !important;
`

const LockedTypeTriggerWrapper = styled.span`
  display: inline-flex;
  flex: 1 0 auto;
`

const LockedTypeBadge = styled.span`
  margin-left: 8px;

  .ui.label {
    display: inline-flex;
    align-items: center;
    vertical-align: middle;
    height: 16px;
    padding: 0 6px;
    font-size: 11px;
    line-height: 16px;
  }
`

const StyledFollowupTypeIcon = styled.i`
  color: ${(props) => (props.active === false ? '#9B9B9B' : '')} !important;
`

export default function ConditionalSetting({
  logic,
  logicIndex,
  setForm,
  isFileUploadPlanEnabled,
}) {
  const { enableFileUploadQuestion } = useFeatureToggle()
  const saveUserActivityLog = useSaveUserActivityLog()
  const user = useUser()
  const isFileUploadFollowUpSelected =
    logic.conditional_question_type === 'follow_up_file_upload_question'
  const canPickFileUploadFollowUp =
    (enableFileUploadQuestion && isFileUploadPlanEnabled === true) || isFileUploadFollowUpSelected
  const shouldUpsellFileUploadFollowUp =
    enableFileUploadQuestion && isFileUploadPlanEnabled === false && !isFileUploadFollowUpSelected

  const companyId = R.path(['selected_workspace', 'id'], user)

  function logFileUploadCta(action) {
    saveUserActivityLog({
      action,
      target: `com_${companyId}`,
      statusCode: 200,
    })
  }

  function handleSelectFollowupMultipleChoiceType(e) {
    setForm(['conditions', logicIndex, 'conditional_question_type'], e.currentTarget.value)

    const shouldAutoFillQuestionText = R.isEmpty(logic.follow_up_multiple_choice_question.text)
    if (shouldAutoFillQuestionText) {
      const questionText = R.path([logic.conditional_question_type, 'text'], logic)
      setForm(
        ['conditions', logicIndex, 'follow_up_multiple_choice_question', 'text'],
        questionText
      )
    }
  }

  function handleSelectFollowupMultiSelectChoiceType(e) {
    setForm(['conditions', logicIndex, 'conditional_question_type'], e.currentTarget.value)

    const shouldAutoFillQuestionText = R.isEmpty(logic.follow_up_multi_select_choice_question.text)
    if (shouldAutoFillQuestionText) {
      const questionText = R.path([logic.conditional_question_type, 'text'], logic)
      setForm(
        ['conditions', logicIndex, 'follow_up_multi_select_choice_question', 'text'],
        questionText
      )
    }
  }

  function handleSelectFollowupCommentType(e) {
    setForm(['conditions', logicIndex, 'conditional_question_type'], e.currentTarget.value)

    const shouldAutoFillQuestionText = R.isEmpty(logic.follow_up_comment_question.text)
    if (shouldAutoFillQuestionText) {
      const questionText = R.path([logic.conditional_question_type, 'text'], logic)
      setForm(['conditions', logicIndex, 'follow_up_comment_question', 'text'], questionText)
    }
  }

  function handleSelectFollowupFileUploadType(e) {
    setForm(['conditions', logicIndex, 'conditional_question_type'], e.currentTarget.value)

    const shouldAutoFillQuestionText = R.isEmpty(logic.follow_up_file_upload_question.text)
    if (shouldAutoFillQuestionText) {
      const questionText = R.path([logic.conditional_question_type, 'text'], logic)
      setForm(['conditions', logicIndex, 'follow_up_file_upload_question', 'text'], questionText)
    }
  }

  return (
    <React.Fragment>
      <Grid.Row className="!pt-0">
        <Grid.Column mobile={16} tablet={4} computer={4}>
          <label className="font-bold">Action</label>
        </Grid.Column>
        <Grid.Column mobile={16} tablet={8} computer={8}>
          <Form.Group grouped>
            <Form.Checkbox
              checked={logic.action === 'display_follow_up_question' ? true : false}
              onChange={() =>
                setForm(['conditions', logicIndex, 'action'], 'display_follow_up_question')
              }
              radio
              label="Display a follow-up question"
            />
            <Form.Checkbox
              checked={logic.action === 'finish_survey' ? true : false}
              onChange={() => setForm(['conditions', logicIndex, 'action'], 'finish_survey')}
              radio
              label="Finish survey"
            />
          </Form.Group>
        </Grid.Column>
      </Grid.Row>

      {logic.action !== 'finish_survey' && (
        <Grid.Row className="!pt-0">
          <Grid.Column width={16}>
            <StyledSegment>
              <Form.Field className="mb-5">
                <label className="font-bold">Follow-up question type</label>
                <div>
                  <FollowUpQuestionTypeGroup fluid>
                    <FollowUpQuestionTypeButton
                      type="button"
                      value="follow_up_comment_question"
                      onClick={handleSelectFollowupCommentType}
                      active={logic.conditional_question_type === 'follow_up_comment_question'}
                      data-testid="follow-up-type-comment-box"
                    >
                      <StyledFollowupTypeIcon
                        active={logic.conditional_question_type === 'follow_up_comment_question'}
                        className="fas fa-quote-left mr-3"
                      />
                      Comment box
                    </FollowUpQuestionTypeButton>
                    {canPickFileUploadFollowUp && (
                      <FollowUpQuestionTypeButton
                        type="button"
                        value="follow_up_file_upload_question"
                        onClick={handleSelectFollowupFileUploadType}
                        active={isFileUploadFollowUpSelected}
                        data-testid="follow-up-type-file-upload"
                      >
                        <StyledFollowupTypeIcon
                          active={isFileUploadFollowUpSelected}
                          className="far fa-paperclip mr-3"
                        />
                        File upload
                      </FollowUpQuestionTypeButton>
                    )}
                    {shouldUpsellFileUploadFollowUp && (
                      <TooltipLabelWithoutIcon
                        descriptionTitle={
                          <span className="flex">
                            File upload <CrownBadge />
                          </span>
                        }
                        descriptionContent="Collect images and files with survey answers. Upgrade to unlock this feature."
                        learnMoreLink={FILE_UPLOAD_LEARN_MORE_LINK}
                        showGotoBillingPage={true}
                        openOnClick={true}
                        position={LOCKED_ITEM_POPUP_POSITION}
                        onPopupOpen={() =>
                          logFileUploadCta(ACTION_NAME.viewed_file_upload_upgrade_cta)
                        }
                        onUpgradeClick={() =>
                          logFileUploadCta(ACTION_NAME.clicked_file_upload_upgrade_cta)
                        }
                        trigger={
                          <LockedTypeTriggerWrapper>
                            <LockedFollowUpQuestionTypeButton
                              type="button"
                              value="follow_up_file_upload_question"
                              active={false}
                              data-testid="follow-up-type-file-upload-locked"
                            >
                              <StyledFollowupTypeIcon
                                active={false}
                                className="far fa-paperclip mr-3"
                              />
                              File upload
                              <LockedTypeBadge>
                                <CrownBadge />
                              </LockedTypeBadge>
                            </LockedFollowUpQuestionTypeButton>
                          </LockedTypeTriggerWrapper>
                        }
                      />
                    )}
                    <FollowUpQuestionTypeButton
                      type="button"
                      value="follow_up_multiple_choice_question"
                      onClick={handleSelectFollowupMultipleChoiceType}
                      active={
                        logic.conditional_question_type === 'follow_up_multiple_choice_question'
                      }
                      data-testid="follow-up-type-multiple-choice"
                    >
                      <StyledFollowupTypeIcon
                        active={
                          logic.conditional_question_type === 'follow_up_multiple_choice_question'
                        }
                        className="fas fa-list mr-3"
                      />
                      Multiple choice
                    </FollowUpQuestionTypeButton>
                    <FollowUpQuestionTypeButton
                      type="button"
                      value="follow_up_multi_select_choice_question"
                      onClick={handleSelectFollowupMultiSelectChoiceType}
                      active={
                        logic.conditional_question_type === 'follow_up_multi_select_choice_question'
                      }
                      data-testid="follow-up-type-multi-select"
                    >
                      <StyledFollowupTypeIcon
                        active={
                          logic.conditional_question_type ===
                          'follow_up_multi_select_choice_question'
                        }
                        className="fas fa-list-check mr-3"
                      />
                      Multi-select
                    </FollowUpQuestionTypeButton>
                  </FollowUpQuestionTypeGroup>
                </div>
              </Form.Field>
              <Form.Field>
                <label>Question</label>
                {logic.conditional_question_type === 'follow_up_comment_question' ? (
                  <Input
                    className="large"
                    name="follow_up_comment_question"
                    type="text"
                    required
                    value={logic.follow_up_comment_question.text}
                    onChange={(e) =>
                      setForm(
                        ['conditions', logicIndex, 'follow_up_comment_question', 'text'],
                        e.target.value
                      )
                    }
                  />
                ) : logic.conditional_question_type === 'follow_up_file_upload_question' ? (
                  <Input
                    className="large"
                    name="follow_up_file_upload_question"
                    type="text"
                    required
                    value={logic.follow_up_file_upload_question.text}
                    onChange={(e) =>
                      setForm(
                        ['conditions', logicIndex, 'follow_up_file_upload_question', 'text'],
                        e.target.value
                      )
                    }
                  />
                ) : logic.conditional_question_type === 'follow_up_multi_select_choice_question' ? (
                  <Input
                    className="large"
                    name="follow_up_multi_select_choice_question"
                    type="text"
                    required
                    value={logic.follow_up_multi_select_choice_question.text}
                    onChange={(e) =>
                      setForm(
                        [
                          'conditions',
                          logicIndex,
                          'follow_up_multi_select_choice_question',
                          'text',
                        ],
                        e.target.value
                      )
                    }
                  />
                ) : (
                  <Input
                    className="large"
                    name="follow_up_multiple_choice_question"
                    type="text"
                    required
                    value={logic.follow_up_multiple_choice_question.text}
                    onChange={(e) =>
                      setForm(
                        ['conditions', logicIndex, 'follow_up_multiple_choice_question', 'text'],
                        e.target.value
                      )
                    }
                  />
                )}
              </Form.Field>
              {logic.conditional_question_type === 'follow_up_comment_question' && (
                <CommentCheckboxes logic={logic} logicIndex={logicIndex} setForm={setForm} />
              )}
              {logic.conditional_question_type === 'follow_up_file_upload_question' && (
                <FileUploadCheckboxes logic={logic} logicIndex={logicIndex} setForm={setForm} />
              )}
              {logic.conditional_question_type === 'follow_up_multiple_choice_question' && (
                <MultipleChoiceCheckboxes logic={logic} logicIndex={logicIndex} setForm={setForm} />
              )}
              {logic.conditional_question_type === 'follow_up_multi_select_choice_question' && (
                <MultiSelectChoiceCheckboxes
                  logic={logic}
                  logicIndex={logicIndex}
                  setForm={setForm}
                />
              )}

              {logic.conditional_question_type === 'follow_up_multiple_choice_question' && (
                <FollowUpChoiceInput
                  logicIndex={logicIndex}
                  choices={R.path(['follow_up_multiple_choice_question', 'choices'], logic) || []}
                  setForm={setForm}
                  questionType={logic.conditional_question_type}
                />
              )}
              {logic.conditional_question_type === 'follow_up_multi_select_choice_question' && (
                <FollowUpChoiceInput
                  logicIndex={logicIndex}
                  choices={
                    R.path(['follow_up_multi_select_choice_question', 'choices'], logic) || []
                  }
                  setForm={setForm}
                  questionType={logic.conditional_question_type}
                />
              )}
            </StyledSegment>
          </Grid.Column>
        </Grid.Row>
      )}
    </React.Fragment>
  )
}

const CommentCheckboxes = ({ logic, logicIndex, setForm }) => {
  return (
    <React.Fragment>
      <StyledFollowupQuestionFormField>
        <Checkbox
          label="Make follow-up question required"
          data-testid="make-follow-up-toggle"
          checked={logic.follow_up_comment_question.is_required}
          onChange={(e, data) => {
            setForm(
              ['conditions', logicIndex, 'follow_up_comment_question', 'is_required'],
              data.checked
            )
            setForm(
              ['conditions', logicIndex, 'follow_up_file_upload_question', 'is_required'],
              data.checked
            )
            setForm(
              ['conditions', logicIndex, 'follow_up_multiple_choice_question', 'is_required'],
              data.checked
            )
            setForm(
              ['conditions', logicIndex, 'follow_up_multi_select_choice_question', 'is_required'],
              data.checked
            )
          }}
        />
      </StyledFollowupQuestionFormField>
      <StyledFollowupQuestionFormField>
        <Checkbox
          label="Finish survey after follow-up question"
          checked={logic.follow_up_comment_question.is_end_survey}
          onChange={(e, data) => {
            setForm(
              ['conditions', logicIndex, 'follow_up_comment_question', 'is_end_survey'],
              data.checked
            )
            setForm(
              ['conditions', logicIndex, 'follow_up_file_upload_question', 'is_end_survey'],
              data.checked
            )
            setForm(
              ['conditions', logicIndex, 'follow_up_multiple_choice_question', 'is_end_survey'],
              data.checked
            )
            setForm(
              ['conditions', logicIndex, 'follow_up_multi_select_choice_question', 'is_end_survey'],
              data.checked
            )
          }}
        />
      </StyledFollowupQuestionFormField>
    </React.Fragment>
  )
}

const FileUploadCheckboxes = ({ logic, logicIndex, setForm }) => {
  return (
    <React.Fragment>
      <StyledFollowupQuestionFormField>
        <Checkbox
          label="Make follow-up question required"
          data-testid="make-follow-up-toggle"
          checked={logic.follow_up_file_upload_question.is_required}
          onChange={(e, data) => {
            setForm(
              ['conditions', logicIndex, 'follow_up_comment_question', 'is_required'],
              data.checked
            )
            setForm(
              ['conditions', logicIndex, 'follow_up_file_upload_question', 'is_required'],
              data.checked
            )
            setForm(
              ['conditions', logicIndex, 'follow_up_multiple_choice_question', 'is_required'],
              data.checked
            )
            setForm(
              ['conditions', logicIndex, 'follow_up_multi_select_choice_question', 'is_required'],
              data.checked
            )
          }}
        />
      </StyledFollowupQuestionFormField>
      <StyledFollowupQuestionFormField>
        <Checkbox
          label="Finish survey after follow-up question"
          checked={logic.follow_up_file_upload_question.is_end_survey}
          onChange={(e, data) => {
            setForm(
              ['conditions', logicIndex, 'follow_up_comment_question', 'is_end_survey'],
              data.checked
            )
            setForm(
              ['conditions', logicIndex, 'follow_up_file_upload_question', 'is_end_survey'],
              data.checked
            )
            setForm(
              ['conditions', logicIndex, 'follow_up_multiple_choice_question', 'is_end_survey'],
              data.checked
            )
            setForm(
              ['conditions', logicIndex, 'follow_up_multi_select_choice_question', 'is_end_survey'],
              data.checked
            )
          }}
        />
      </StyledFollowupQuestionFormField>
    </React.Fragment>
  )
}

const MultipleChoiceCheckboxes = ({ logic, logicIndex, setForm }) => {
  return (
    <React.Fragment>
      <StyledFollowupQuestionFormField>
        <Checkbox
          label="Make follow-up question required"
          data-testid="make-follow-up-toggle"
          checked={logic.follow_up_multiple_choice_question.is_required}
          onChange={(e, data) => {
            setForm(
              ['conditions', logicIndex, 'follow_up_comment_question', 'is_required'],
              data.checked
            )
            setForm(
              ['conditions', logicIndex, 'follow_up_file_upload_question', 'is_required'],
              data.checked
            )
            setForm(
              ['conditions', logicIndex, 'follow_up_multiple_choice_question', 'is_required'],
              data.checked
            )
            setForm(
              ['conditions', logicIndex, 'follow_up_multi_select_choice_question', 'is_required'],
              data.checked
            )
          }}
        />
      </StyledFollowupQuestionFormField>
      <StyledFollowupQuestionFormField>
        <Checkbox
          label="Finish survey after follow-up question"
          checked={logic.follow_up_multiple_choice_question.is_end_survey}
          onChange={(e, data) => {
            setForm(
              ['conditions', logicIndex, 'follow_up_comment_question', 'is_end_survey'],
              data.checked
            )
            setForm(
              ['conditions', logicIndex, 'follow_up_file_upload_question', 'is_end_survey'],
              data.checked
            )
            setForm(
              ['conditions', logicIndex, 'follow_up_multiple_choice_question', 'is_end_survey'],
              data.checked
            )
            setForm(
              ['conditions', logicIndex, 'follow_up_multi_select_choice_question', 'is_end_survey'],
              data.checked
            )
          }}
        />
      </StyledFollowupQuestionFormField>
    </React.Fragment>
  )
}

const MultiSelectChoiceCheckboxes = ({ logic, logicIndex, setForm }) => {
  return (
    <React.Fragment>
      <StyledFollowupQuestionFormField>
        <Checkbox
          label="Make follow-up question required"
          data-testid="make-follow-up-toggle"
          checked={logic.follow_up_multi_select_choice_question.is_required}
          onChange={(e, data) => {
            setForm(
              ['conditions', logicIndex, 'follow_up_comment_question', 'is_required'],
              data.checked
            )
            setForm(
              ['conditions', logicIndex, 'follow_up_file_upload_question', 'is_required'],
              data.checked
            )
            setForm(
              ['conditions', logicIndex, 'follow_up_multiple_choice_question', 'is_required'],
              data.checked
            )
            setForm(
              ['conditions', logicIndex, 'follow_up_multi_select_choice_question', 'is_required'],
              data.checked
            )
          }}
        />
      </StyledFollowupQuestionFormField>
      <StyledFollowupQuestionFormField>
        <Checkbox
          label="Finish survey after follow-up question"
          checked={logic.follow_up_multi_select_choice_question.is_end_survey}
          onChange={(e, data) => {
            setForm(
              ['conditions', logicIndex, 'follow_up_comment_question', 'is_end_survey'],
              data.checked
            )
            setForm(
              ['conditions', logicIndex, 'follow_up_file_upload_question', 'is_end_survey'],
              data.checked
            )
            setForm(
              ['conditions', logicIndex, 'follow_up_multiple_choice_question', 'is_end_survey'],
              data.checked
            )
            setForm(
              ['conditions', logicIndex, 'follow_up_multi_select_choice_question', 'is_end_survey'],
              data.checked
            )
          }}
        />
      </StyledFollowupQuestionFormField>
    </React.Fragment>
  )
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/organisms/QuestionConditionModal/ConditionalSetting.js